import { useTranslation } from "react-i18next";
import { Collapsible, CollapsibleContent, CollapsibleTrigger, cn } from "@tellescope/ui";
import { ChevronDown, Eye } from "lucide-react";
import { ConditionBuilder, type VisibilityCondition } from "@tellescope/visibility-conditions";

interface ProductVisibilitySectionProps {
  visibility: { condition: VisibilityCondition } | undefined;
  onChange: (condition: VisibilityCondition | undefined) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Per-product visibility condition editor, mirroring portal-web's block-level VisibilitySection —
 * same ConditionBuilder, scoped to a single product instead of a whole block.
 */
export const ProductVisibilitySection = ({
  visibility,
  onChange,
  open,
  onOpenChange,
}: ProductVisibilitySectionProps) => {
  const { t } = useTranslation("shop");

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="border-t pt-3 mt-3">
      <CollapsibleTrigger className="flex items-center gap-2 w-full text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
        <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
        <Eye className="h-4 w-4" />
        {t("Product Visibility Conditions")}
        {visibility && (
          <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">{t("Conditional")}</span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3">
        <p className="text-xs text-muted-foreground mb-3">
          {t("Set conditions to show this product to specific patients based on their data.")}
        </p>
        <ConditionBuilder value={visibility?.condition} onChange={onChange} />
      </CollapsibleContent>
    </Collapsible>
  );
};

// Future: a "Recommended Rail Visibility Conditions" section will live in this same collapsible
// area, letting admins separately condition whether a product appears in the recommended rail.
